import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';

export interface IdNameDto {
	id: string;
	name: string;
}

export interface RegionDto {
	id: string;
	worldId: string;
	name: string;
	description: string | null;
	imageAssetId: string | null;
	mapAssetId: string | null;
	pdfAssetId: string | null;
	gmNotes: string | null;
	foundryId: string | null;
	regionalGods: IdNameDto[];
	regionalGodIds: string[];
	createdAt: string;
	updatedAt: string;
}

export interface RegionListItemDto {
	id: string;
	name: string;
	foundryId: string | null;
	regionalGods: IdNameDto[];
	updatedAt: string;
}

export interface RegionsListResponse {
	regions: RegionListItemDto[];
	total: number;
}

export interface RegionGetResponse {
	region: RegionDto;
}

export interface ListRegionsParams {
	search?: string;
	godId?: string;
	sortBy?: string;
	sortDirection?: 'asc' | 'desc';
	limit?: number;
	offset?: number;
	format?: 'list' | 'full';
}

export interface CreateRegionRequest {
	name: string;
	description?: string | null;
	imageAssetId?: string | null;
	mapAssetId?: string | null;
	pdfAssetId?: string | null;
	gmNotes?: string | null;
	foundryId?: string | null;
	regionalGodIds?: string[] | null;
}

export interface UpdateRegionRequest {
	name: string;
	description?: string | null;
	imageAssetId?: string | null;
	mapAssetId?: string | null;
	pdfAssetId?: string | null;
	gmNotes?: string | null;
	foundryId?: string | null;
	regionalGodIds?: string[] | null;
}

@Injectable({ providedIn: 'root' })
export class RegionsService {
	constructor(private readonly http: HttpClient) {}

	list(worldId: string, params?: ListRegionsParams): Observable<RegionsListResponse> {
		let httpParams = new HttpParams();

		if (params?.search) httpParams = httpParams.set('search', params.search);
		if (params?.godId) httpParams = httpParams.set('godId', params.godId);
		if (params?.sortBy) httpParams = httpParams.set('sortBy', params.sortBy);
		if (params?.sortDirection) httpParams = httpParams.set('sortDirection', params.sortDirection);
		if (params?.limit !== undefined) httpParams = httpParams.set('limit', params.limit.toString());
		if (params?.offset !== undefined) httpParams = httpParams.set('offset', params.offset.toString());
		if (params?.format) httpParams = httpParams.set('format', params.format);

		return this.http.get<RegionsListResponse>(`${environment.api.baseUrl}/v1/worlds/${worldId}/regions`, {
			params: httpParams,
		});
	}

	get(worldId: string, regionId: string): Observable<RegionGetResponse> {
		return this.http.get<RegionGetResponse>(`${environment.api.baseUrl}/v1/worlds/${worldId}/regions/${regionId}`);
	}

	create(worldId: string, request: CreateRegionRequest): Observable<RegionGetResponse> {
		return this.http.post<RegionGetResponse>(`${environment.api.baseUrl}/v1/worlds/${worldId}/regions`, request);
	}

	update(worldId: string, regionId: string, request: UpdateRegionRequest): Observable<RegionGetResponse> {
		return this.http.put<RegionGetResponse>(
			`${environment.api.baseUrl}/v1/worlds/${worldId}/regions/${regionId}`,
			request
		);
	}

	delete(worldId: string, regionId: string): Observable<void> {
		return this.http.delete<void>(`${environment.api.baseUrl}/v1/worlds/${worldId}/regions/${regionId}`);
	}
}
